import React from "react";

import { DeprecatedTooltip } from "../atoms/deprecated-tooltip";
import { generateAlphaNumericRandomStr } from "../../utils/random-string";
import { useClipboardText } from "../../utils/use-clipboard";

type DisabledBy =
  | "PUBLISHED_PROCEDURE"
  | "ACTIVE_SEARCH_INDEX"
  | "REFERRED_BY_PROC_MGT";

type Props = {
  value?: string;
  isValid?: boolean;
  disabledBy?: DisabledBy;
  onChange: (passcode: string) => void;
};

export const ProcedurePasscodeSetting: React.FC<Props> = ({
  value,
  isValid = true,
  onChange,
  disabledBy,
}) => {
  const [copyFeedback, setCopyFeedback] = React.useState<string | null>(null);

  const handleButtonClick = React.useCallback(() => {
    onChange(generateAlphaNumericRandomStr({ length: 10 }));
    setCopyFeedback(null);
  }, [onChange, setCopyFeedback]);
  const handleDeleteIconClick = React.useCallback(() => {
    onChange("");
    setCopyFeedback(null);
  }, [onChange, setCopyFeedback]);

  const { buttonRef } = useClipboardText(value ?? "", () => {
    setCopyFeedback("パスコードをクリップボードへコピーしました。");
  });
  return (
    <div className="field">
      <div className="label">
        パスコードの要求
        <DeprecatedTooltip text={explanation} position="right" />
        {typeof disabledBy === "string" && (
          <p className="help has-text-weight-normal">
            {`※ ${messageMapForDisabled[disabledBy]}`}
          </p>
        )}
        {typeof copyFeedback === "string" && (
          <p className="help has-text-weight-normal has-text-success">
            {copyFeedback}
          </p>
        )}
      </div>

      {/* Wrap `filed has-addons` with `control` for correct view */}
      {/* https://github.com/jgthms/bulma/issues/777#issuecomment-393245473 */}
      <div className="control">
        <div className="field has-addons">
          <div className="control">
            <button
              className="button"
              disabled={typeof disabledBy === "string"}
              onClick={handleButtonClick}
            >
              パスコードを生成する
            </button>
          </div>
          <div className="control has-icons-right">
            <input
              className={`input ${isValid ? "" : "is-danger"}`}
              value={typeof value === "string" ? value : ""}
              readOnly
            />
            {typeof disabledBy !== "string" && typeof value === "string" && (
              <span
                className="icon is-small is-right"
                onClick={handleDeleteIconClick}
                style={{
                  // bulmaのデフォルトのスタイルにより、アイコンがクリックできなくなってしまうので、
                  // クリックできるよう上書きする。
                  // https://github.com/jgthms/bulma/issues/1965#issuecomment-400612852
                  //
                  // IEだと `pointer-events: initial`が無効だったので、`auto`を用いている。
                  pointerEvents: "auto",
                }}
              >
                <i className="fas fa-times"></i>
              </span>
            )}
            <p className={`help is-danger ${isValid ? "is-hidden" : ""}`}>
              パスコードは6文字以上、16文字以下の数字またはアルファベットで入力してください。
            </p>
          </div>
          <div
            className={`control ${
              typeof value === "string" ? "" : "is-hidden"
            }`}
          >
            <button
              className="button"
              ref={buttonRef}
              onClick={(e) => {
                e.preventDefault();
              }}
              disabled={typeof value !== "string"}
            >
              コピー
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const noticeForSearchIndexConfig =
  "「検索エンジンの結果ページに表示しないようにする」設定が有効なときのみ利用可能です。";
const noticeForPublishedProcedure =
  "手続きを一度公開すると、パスコードは編集できません。";
const explanation = [
  "公開した手続きフォームへのアクセスに対して、設定したパスコードを要求することができます。",
  "正しいパスコードが入力されない限り、ユーザーはフォームを利用できません。",
  noticeForSearchIndexConfig,
  noticeForPublishedProcedure,
].join("\n");

const messageMapForDisabled: Record<DisabledBy, string> = {
  PUBLISHED_PROCEDURE: noticeForPublishedProcedure,
  ACTIVE_SEARCH_INDEX: noticeForSearchIndexConfig,
  REFERRED_BY_PROC_MGT:
    "手続きマスタに掲載されている場合、パスコード設定は有効化できません。",
} as const;
